// The resolved-payload store behind the Customer Insights data seam. It exists so `mutate` is a
// real drop rather than a stub: fixtures are resolved once and memoised, so after an invalidation
// the next read re-resolves and any consumer still holding the previous payload sees it gone. When
// the seam is swapped for SWR this module collapses into `mutate()` calls against the SWR cache.
// PLA-8815.

import { cardFixtures } from '../fixtures/cards';
import { IntelligenceFilterId, IntelligenceFilters, IntelligenceFixture } from '../types';

// card key -> cache key (tenant + filters) -> resolved payload. Nesting by card key keeps
// invalidation, which is always per card and across every tenant/filter combination, a single
// delete.
const resolvedCards = new Map<string, Map<string, IntelligenceFixture>>();

// Dropping an entry is not enough on its own: the drop notifies every subscriber, and a mounted
// `useCardData` resolves during render, so the card the notification was meant to clear would
// re-resolve and re-cache the payload on the very next commit. Revocation therefore has to be a
// state of the card, not a one-off delete — `readCard` refuses to resolve while the key is in here,
// so the drop holds however many renders follow and whichever consumer asks. PLA-8815.
const revokedCards = new Set<string>();

// Deleting an entry is only half of a drop: a mounted consumer has to re-resolve too, which SWR
// gets from its shared-cache subscription. The version counter is that subscription for this seam.
let cacheVersion = 0;
const listeners = new Set<() => void>();

const notifyCacheChanged = (): void => {
  cacheVersion += 1;
  listeners.forEach(listener => listener());
};

export const subscribeToCardCache = (listener: () => void): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

export const getCardCacheVersion = (): number => cacheVersion;

// A cache entry is only valid for the filters it was resolved under, so every dynamic parameter has
// to be part of the key (`conventions/data-fetching.md § Critical Rules`) — the active tenant
// included: `resolvedCards` is module-level and an in-SPA tenant switch never reloads the page, so
// without it tenant A's payload would be served to tenant B (`conventions/multi-tenancy.md`).
export const cacheKeyFor = (filters?: IntelligenceFilters, tenant?: string): string => {
  const filterParts = filters
    ? (Object.keys(filters) as IntelligenceFilterId[])
        .filter(id => filters[id] !== undefined)
        .sort()
        .map(id => `${id}=${filters[id]}`)
    : [];

  return [`tenant=${tenant ?? ''}`, ...filterParts].join('&');
};

// Deep, not a spread: with shared nested objects a card that ranks its own metrics
// (`data.metrics.sort(...)`) would reorder the shipped fixture module for the rest of the session,
// and an invalidation would hand out a fresh top-level object still aliasing the mutated children.
const cloneFixture = (fixture: IntelligenceFixture): IntelligenceFixture => ({
  ...fixture,
  state: { ...fixture.state },
  metrics: fixture.metrics.map(metric => ({
    ...metric,
    delta: metric.delta && { ...metric.delta },
    sparkline: metric.sparkline && [...metric.sparkline]
  })),
  confidence: fixture.confidence && {
    ...fixture.confidence,
    reasons: [...fixture.confidence.reasons]
  },
  evidence: fixture.evidence && {
    ...fixture.evidence,
    rows: fixture.evidence.rows.map(row => ({ ...row }))
  }
});

export const isCardRevoked = (cardKey: string): boolean => revokedCards.has(cardKey);

export const readCard = (cardKey: string, cacheKey = ''): IntelligenceFixture | undefined => {
  // Own-property check, not a truthiness test on the index: `useCardData('constructor')` would
  // otherwise resolve a prototype member into a payload with no state and no error.
  if (!Object.prototype.hasOwnProperty.call(cardFixtures, cardKey)) {
    return undefined;
  }

  if (revokedCards.has(cardKey)) {
    return undefined;
  }

  let byCacheKey = resolvedCards.get(cardKey);

  if (!byCacheKey) {
    byCacheKey = new Map<string, IntelligenceFixture>();
    resolvedCards.set(cardKey, byCacheKey);
  }

  const cached = byCacheKey.get(cacheKey);

  if (cached) {
    return cached;
  }

  const resolved = cloneFixture(cardFixtures[cardKey]);
  byCacheKey.set(cacheKey, resolved);

  return resolved;
};

// Read-only probe: unlike `readCard` it never resolves, so a caller can assert what the cache is
// actually holding.
export const peekCachedCard = (cardKey: string, cacheKey = ''): IntelligenceFixture | undefined =>
  resolvedCards.get(cardKey)?.get(cacheKey);

export const invalidateCard = (cardKey: string): void => {
  resolvedCards.delete(cardKey);
  notifyCacheChanged();
};

export const invalidateCards = (cardKeys: string[]): void => {
  cardKeys.forEach(cardKey => resolvedCards.delete(cardKey));
  notifyCacheChanged();
};

// Revocation and a refresh are the same delete but the opposite intent, which is why they are two
// exports: `invalidateCard*` wants the payload resolved again, `revokeCards` must not let that
// happen until the grant comes back.
export const revokeCards = (cardKeys: string[]): void => {
  cardKeys.forEach(cardKey => revokedCards.add(cardKey));
  invalidateCards(cardKeys);
};

export const restoreCards = (cardKeys: string[]): void => {
  const restored = cardKeys.filter(cardKey => revokedCards.delete(cardKey));

  // Only when something actually changed: this runs on every resolved `visible`, and an
  // unconditional notification would re-render every subscriber on every card mount.
  if (restored.length > 0) {
    notifyCacheChanged();
  }
};

// The per-card revocation drop in `useCardVisibility` can only fire for a card that is mounted when
// the authorisation refresh lands. `syncCardCacheGrants` below is the session-scoped half: the whole
// cache goes the moment the Customer Intelligence grant set changes, whichever cards are on screen.
let lastGrantSignature: string | null = null;

// The seam's equivalent of `useSWRUtils().clearCacheData()`. Importing this module from the sign-out
// and tenant-switch components would pull the fixtures into the main chunk, so the eviction is
// driven from inside the lazy Intelligence chunk instead: `syncCardCacheGrants` below on a grant
// change, and the tenant in every cache key for the switch itself.
// Deliberately leaves `revokedCards` alone. React runs child effects before parent effects, so this
// fires AFTER the per-card `revokeCards` of the same authorisation refresh; clearing the marks here
// would re-authorise every card the cards themselves had just revoked, and the notification below
// would immediately resolve their payloads back. Only `restoreCards`, driven by a card resolving to
// visible again, lifts a revocation.
export const clearCardCache = (): void => {
  resolvedCards.clear();
  lastGrantSignature = null;
  notifyCacheChanged();
};

export const syncCardCacheGrants = (grantSignature: string): void => {
  if (lastGrantSignature === grantSignature) {
    return;
  }

  clearCardCache();
  lastGrantSignature = grantSignature;
};
